import { useState, useRef, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../../shell/shared';
import { uploadToStorage } from '../../../lib/upload-to-storage';
import {
  CameraCaptureModal,
  isCameraCaptureSupported,
} from '../../../components/CameraCaptureModal';
import { type FileTag, type PatientFileItem } from './consultation-data';

// Tag chips available in the upload drawer
const UPLOAD_TAGS: { label: string; value: FileTag }[] = [
  { label: 'Images', value: 'images' },
  { label: 'Documents', value: 'documents' },
  { label: 'Consent', value: 'consent' },
  { label: 'Lab', value: 'lab' },
  { label: 'Before/After', value: 'before_after' },
  { label: 'Other', value: 'other' },
];

interface UploadDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired once per successfully uploaded file. */
  onUpload: (file: PatientFileItem) => void;
  orgId: string;
  patientId: string;
  brandTokens: Record<string, string>;
}

export function UploadDrawer({
  open,
  onOpenChange,
  onUpload,
  orgId,
  patientId,
  brandTokens,
}: UploadDrawerProps) {
  const [selectedTag, setSelectedTag] = useState<FileTag>('other');
  const [description, setDescription] = useState('');
  const [starAfterUpload, setStarAfterUpload] = useState(false);
  // Multi-file: each drop / browse appends to this list. The user can
  // remove individual files via the trash icon before clicking Upload.
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const cameraSupported = isCameraCaptureSupported();

  if (!open) return null;

  // ─── File-collection helpers ──────────────────────────────────────────

  const appendFiles = (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const list = Array.from(incoming);
    if (list.length === 0) return;
    setRawFiles((prev) => {
      // Dedupe by (name, size, lastModified) — best-effort, same File
      // dropped twice from the same source should not double-upload.
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      const merged = [...prev];
      for (const f of list) {
        const key = `${f.name}|${f.size}|${f.lastModified}`;
        if (!seen.has(key)) {
          merged.push(f);
          seen.add(key);
        }
      }
      return merged;
    });
    setError(null);
  };

  const removeAt = (idx: number) => {
    setRawFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─── Event handlers ───────────────────────────────────────────────────

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    appendFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    appendFiles(e.target.files);
    // Reset the input value so re-selecting the same file fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (rawFiles.length === 0) return;
    setUploading(true);
    setError(null);
    setProgress({ done: 0, total: rawFiles.length });

    const failures: { name: string; reason: string }[] = [];
    const createdAt = new Date().toISOString();

    for (let i = 0; i < rawFiles.length; i++) {
      const file = rawFiles[i];
      const fileId = crypto.randomUUID();
      const isImage = file.type.startsWith('image/');

      try {
        const result = await uploadToStorage({
          orgId,
          patientId,
          file,
          fileId,
        });

        const newFile: PatientFileItem = {
          id: fileId,
          patientId,
          kind: isImage ? 'image' : 'document',
          filename: result.filename,
          tag: selectedTag,
          createdAt,
          starred: starAfterUpload,
          mimeType: result.mimeType,
          description: description || undefined,
          url: result.downloadUrl,
        };
        onUpload(newFile);
      } catch (err) {
        failures.push({
          name: file.name,
          reason: err instanceof Error ? err.message : 'Upload failed',
        });
      } finally {
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    }

    setUploading(false);

    if (failures.length === 0) {
      // Reset and close
      setRawFiles([]);
      setDescription('');
      setStarAfterUpload(false);
      setSelectedTag('other');
      setProgress({ done: 0, total: 0 });
      onOpenChange(false);
    } else {
      // Keep the drawer open and surface which files failed. Successful
      // uploads have already been flushed to the parent.
      const remaining = rawFiles.filter((f) => failures.some((x) => x.name === f.name));
      setRawFiles(remaining);
      setError(
        `Uploaded ${rawFiles.length - failures.length} of ${rawFiles.length}. Failed: ${failures
          .map((f) => f.name)
          .join(', ')}`,
      );
      setProgress({ done: 0, total: 0 });
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setRawFiles([]);
    setDescription('');
    setStarAfterUpload(false);
    setError(null);
    setProgress({ done: 0, total: 0 });
    onOpenChange(false);
  };

  const primary = brandTokens['--color-primary'] || '#78716c';

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 50,
        }}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          maxWidth: '100vw',
          background: 'var(--bg-surface)',
          boxShadow: 'var(--shadow-xl)',
          zIndex: 51,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-5) var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Upload
          </h2>
          <button
            type="button"
            onClick={handleClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--sp-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* Drop zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? primary : 'var(--border-input)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-8) var(--sp-6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              cursor: 'pointer',
              background: dragOver ? 'rgba(120,113,108,0.04)' : 'var(--bg-surface-2)',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <div style={{ color: 'var(--text-muted)' }}>
              <NavIcon name="upload" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {rawFiles.length > 0
                ? `${rawFiles.length} file${rawFiles.length === 1 ? '' : 's'} selected`
                : 'Drag & drop files here'}
            </span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {rawFiles.length > 0 ? 'Drop more or click to add' : 'or click to browse'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Take a photo with the device camera (adds to the list below) */}
          {cameraSupported && (
            <Button
              variant="outline"
              size="md"
              leftIcon={<NavIcon name="camera" />}
              onClick={() => setCameraOpen(true)}
            >
              Take photo
            </Button>
          )}

          {/* Selected file list */}
          {rawFiles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              {rawFiles.map((f, idx) => (
                <div
                  key={`${f.name}-${f.size}-${f.lastModified}-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-2) var(--sp-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <NavIcon name={f.type.startsWith('image/') ? 'image' : 'file-text'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text)',
                        fontWeight: 'var(--font-weight-medium)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={f.name}
                    >
                      {f.name}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {formatBytes(f.size)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    disabled={uploading}
                    title="Remove"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      opacity: uploading ? 0.5 : 1,
                    }}
                  >
                    <NavIcon name="x" />
                  </button>
                </div>
              ))}
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                }}
              >
                The selected tag and description apply to every file in this batch.
              </div>
            </div>
          )}

          {/* Tag selection */}
          <div>
            <label style={labelStyle}>Tag</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
              {UPLOAD_TAGS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSelectedTag(t.value)}
                  style={{
                    height: 30,
                    padding: '0 var(--sp-3)',
                    borderRadius: 'var(--radius-full)',
                    border:
                      selectedTag === t.value
                        ? `1.5px solid ${primary}`
                        : '1px solid var(--border-input)',
                    background: selectedTag === t.value ? `${primary}10` : 'var(--bg-surface)',
                    color: selectedTag === t.value ? primary : 'var(--text)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Star toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
            }}
          >
            <div
              onClick={(e) => {
                e.preventDefault();
                setStarAfterUpload(!starAfterUpload);
              }}
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                background: starAfterUpload ? primary : 'var(--border-input)',
                position: 'relative',
                transition: 'background 0.2s',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: 2,
                  left: starAfterUpload ? 20 : 2,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }}
              />
            </div>
            Star after upload
          </label>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              margin: '0 var(--sp-6)',
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fef2f2',
              color: '#dc2626',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-6)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
          }}
        >
          <Button variant="outline" size="md" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleUpload}
            disabled={rawFiles.length === 0 || uploading}
          >
            {uploading
              ? `Uploading ${progress.done + 1} of ${progress.total}…`
              : rawFiles.length > 1
                ? `Upload ${rawFiles.length} files`
                : 'Upload'}
          </Button>
        </div>
      </div>

      <CameraCaptureModal
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        filenamePrefix="photo"
        title="Take photo"
        onCapture={(file) => appendFiles([file])}
      />
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 'var(--sp-2)',
};
